import type { JSX } from 'react';
import type { ApImportFormat, ApVorschau } from '@asas/domain';

import {
  FORMAT_FELDER,
  hatAenderungen,
  KLAERUNG_TEXTE,
  KONFLIKT_TEXTE,
  phasenText,
} from './ap-import';
import type { FormatEntwurf, FormatFeld } from './ap-import';

/**
 * Die REINE Ansicht des Imports der Stammarbeitspakete (AC-302): lädt nichts,
 * ruft nichts auf. Laden, Prüfen, Übernehmen und Fehler stehen in
 * `ApImportBereich`.
 */
export interface ApImportAnsichtProps {
  readonly geoeffnet: boolean;
  readonly onOeffnen: () => void;
  /** `undefined`: noch nicht geladen; `null`: kein Format eingerichtet. */
  readonly format: ApImportFormat | null | undefined;
  readonly laedt: boolean;
  /** Prüfen oder Übernehmen läuft. */
  readonly sendet: boolean;
  /** Nach einer Ablehnung (403) bis zum Neuladen der Seite gesperrt. */
  readonly gesperrt: boolean;
  readonly ladefehler: string | null;
  readonly dateiName: string | null;
  readonly vorschau: ApVorschau | null;
  readonly fehler: string | null;
  readonly meldung: string | null;
  readonly anmeldeUrl: string | null;
  readonly onNeuLaden: () => void;
  readonly onDatei: (datei: File | null) => void;
  readonly onPruefen: () => void;
  readonly onUebernehmen: () => void;
  readonly onNeuAnmelden: () => void;
  /** `null`: ohne `tenant:configure` — das Formular wird nicht angeboten. */
  readonly formular: FormatFormularProps | null;
}

export interface FormatFormularProps {
  readonly entwurf: FormatEntwurf;
  readonly geaendert: boolean;
  readonly sendet: boolean;
  readonly meldungen: Partial<Record<FormatFeld, string>>;
  readonly hinweise: Partial<Record<FormatFeld, string>>;
  readonly fehler: string | null;
  readonly meldung: string | null;
  readonly onFeld: (feld: FormatFeld, wert: string) => void;
  readonly onSpeichern: () => void;
  readonly onVerwerfen: () => void;
}

const FELD_BESCHRIFTUNG: Readonly<Record<FormatFeld, string>> = {
  blattPraefix: 'Katalogblätter beginnen mit',
  kopfZeilen: 'Kopfzeilen',
  spalteNr: 'Spalte Nr',
  spalteBezeichnung: 'Spalte Bezeichnung',
  nrMuster: 'Muster der Nr (regulärer Ausdruck)',
};

function Zahl({ titel, wert }: { titel: string; wert: number | string }): JSX.Element {
  return (
    <>
      <dt>{titel}</dt>
      <dd>{wert}</dd>
    </>
  );
}

function Vorschau({ vorschau }: { vorschau: ApVorschau }): JSX.Element {
  return (
    <div aria-label="Vorschau">
      <h3>Vorschau: {vorschau.dateiName}</h3>
      {vorschau.bereitsUebernommen !== null && (
        <p>
          Dieselbe Datei wurde bereits am {vorschau.bereitsUebernommen.am} als „
          {vorschau.bereitsUebernommen.dateiName}“ übernommen (
          {vorschau.bereitsUebernommen.eintraege} Einträge geschrieben).
        </p>
      )}
      <dl>
        <Zahl titel="Zeilen mit Inhalt" wert={vorschau.zeilen} />
        <Zahl
          titel="Erkannte Pakete je Blatt"
          wert={vorschau.jeBlatt.map((b) => `${b.blatt}: ${String(b.pakete)}`).join('; ') || '—'}
        />
        <Zahl titel="Pakete je Gruppe (nach Zusammenführen)" wert={vorschau.pakete} />
        <Zahl titel="Gewerke" wert={vorschau.gewerke} />
        <Zahl titel="Neu" wert={vorschau.neu.length} />
        <Zahl titel="Unverändert" wert={vorschau.unveraendert} />
        <Zahl titel="Geändert" wert={vorschau.geaendert.length} />
        <Zahl titel="In der Datei nicht mehr enthalten" wert={vorschau.fehlend.length} />
        <Zahl titel="Archiviert in der Bibliothek" wert={vorschau.archiviert.length} />
        <Zahl titel="Konflikte" wert={vorschau.konflikte.length} />
        <Zahl titel="Klärungsfälle" wert={vorschau.klaerungsfaelle.length} />
        <Zahl titel="Übergangene Zeilen" wert={vorschau.uebergangen} />
        <Zahl
          titel="Nicht gelesene Blätter"
          wert={vorschau.nichtGeleseneBlaetter.join(', ') || '—'}
        />
      </dl>

      {vorschau.konflikte.length > 0 && (
        <>
          <h4>Konflikte — werden nicht übernommen</h4>
          <table>
            <thead>
              <tr>
                <th>Blatt</th>
                <th>Zeile</th>
                <th>Nr</th>
                <th>Grund</th>
              </tr>
            </thead>
            <tbody>
              {vorschau.konflikte.map((k) => (
                <tr key={`${k.blatt}:${String(k.zeile)}:${k.grund}`}>
                  <td>{k.blatt}</td>
                  <td>{k.zeile}</td>
                  <td>{k.nr ?? '—'}</td>
                  <td>{KONFLIKT_TEXTE[k.grund]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {vorschau.klaerungsfaelle.length > 0 && (
        <>
          <h4>Klärungsliste — ohne eindeutige Leistungsphasengruppe, nicht übernommen</h4>
          <table>
            <thead>
              <tr>
                <th>Blatt</th>
                <th>Zeile</th>
                <th>Nr</th>
                <th>Bezeichnung</th>
                <th>Phasen</th>
                <th>Grund</th>
              </tr>
            </thead>
            <tbody>
              {vorschau.klaerungsfaelle.map((f) => (
                <tr key={`${f.blatt}:${String(f.zeile)}`}>
                  <td>{f.blatt}</td>
                  <td>{f.zeile}</td>
                  <td>{f.nr}</td>
                  <td>{f.bezeichnung ?? '—'}</td>
                  <td>{phasenText(f.phasen)}</td>
                  <td>{KLAERUNG_TEXTE[f.grund]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {vorschau.geaendert.length > 0 && (
        <>
          <h4>Geändert</h4>
          <ul>
            {vorschau.geaendert.map((g) => (
              <li key={`${g.code}:${g.leistungsphasen.join()}`}>
                {g.code} ({phasenText(g.leistungsphasen)}): „{g.vorher.name}“ → „{g.nachher.name}“
              </li>
            ))}
          </ul>
        </>
      )}

      {vorschau.fehlend.length > 0 && (
        <>
          <h4>In der Datei nicht mehr enthalten — bleiben in der Bibliothek</h4>
          <ul>
            {vorschau.fehlend.map((e) => (
              <li key={`${e.code}:${e.leistungsphasen.join()}`}>
                {e.code} {e.name} ({phasenText(e.leistungsphasen)})
              </li>
            ))}
          </ul>
        </>
      )}

      {vorschau.archiviert.length > 0 && (
        <>
          <h4>Archiviert in der Bibliothek — werden nicht angefasst</h4>
          <ul>
            {vorschau.archiviert.map((e) => (
              <li key={`${e.code}:${e.leistungsphasen.join()}`}>
                {e.code} {e.name} ({phasenText(e.leistungsphasen)})
              </li>
            ))}
          </ul>
        </>
      )}

      {vorschau.neu.length > 0 && (
        <details>
          <summary>Neu ({vorschau.neu.length})</summary>
          <ul>
            {vorschau.neu.map((e) => (
              <li key={`${e.code}:${e.leistungsphasen.join()}`}>
                {e.code} {e.name} ({phasenText(e.leistungsphasen)})
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function FormatFormular(props: FormatFormularProps & { gesperrt: boolean }): JSX.Element {
  return (
    <form
      aria-label="Format einrichten"
      onSubmit={(event) => {
        event.preventDefault();
        props.onSpeichern();
      }}
    >
      <h3>Format einrichten</h3>
      {FORMAT_FELDER.map((feld) => {
        const meldung = props.meldungen[feld];
        const hinweis = props.hinweise[feld];

        return (
          <p key={feld}>
            <label>
              {FELD_BESCHRIFTUNG[feld]}{' '}
              <input
                name={feld}
                value={props.entwurf[feld]}
                disabled={props.gesperrt}
                aria-invalid={meldung !== undefined}
                onChange={(event) => {
                  props.onFeld(feld, event.target.value);
                }}
              />
            </label>
            {meldung !== undefined && <span role="alert">{meldung}</span>}
            {hinweis !== undefined && <span>{hinweis}</span>}
          </p>
        );
      })}
      {props.fehler !== null && <p role="alert">{props.fehler}</p>}
      <p role="status">{props.meldung}</p>
      <button type="submit" disabled={props.gesperrt || !props.geaendert}>
        Format speichern
      </button>{' '}
      <button
        type="button"
        disabled={props.gesperrt || !props.geaendert}
        onClick={props.onVerwerfen}
      >
        Verwerfen
      </button>
    </form>
  );
}

export function ApImportAnsicht(props: ApImportAnsichtProps): JSX.Element {
  const beschaeftigt = props.laedt || props.sendet || props.gesperrt;
  const eingerichtet = props.format !== null && props.format !== undefined;

  return (
    <section aria-labelledby="ap-import-titel">
      <h2 id="ap-import-titel">Stammarbeitspakete importieren</h2>
      {!props.geoeffnet ? (
        <button type="button" onClick={props.onOeffnen}>
          Import öffnen
        </button>
      ) : (
        <>
          {props.ladefehler !== null && <p role="alert">{props.ladefehler}</p>}
          {props.fehler !== null && <p role="alert">{props.fehler}</p>}
          <p role="status">{props.meldung}</p>
          {props.anmeldeUrl !== null && (
            <p>
              <a href={props.anmeldeUrl} onClick={props.onNeuAnmelden}>
                Neu anmelden
              </a>
            </p>
          )}
          <p>
            <button type="button" disabled={beschaeftigt} onClick={props.onNeuLaden}>
              Neu laden
            </button>
          </p>

          {props.format === null && (
            <p>
              Für diesen Mandanten ist noch kein Importformat eingerichtet.
              {props.formular === null ? ' Bitte die Systemadministration ansprechen.' : ''}
            </p>
          )}

          {eingerichtet && (
            <>
              <p>
                <label>
                  Datei (.xlsx){' '}
                  <input
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    disabled={beschaeftigt}
                    onChange={(event) => {
                      props.onDatei(event.target.files?.[0] ?? null);
                    }}
                  />
                </label>{' '}
                <button
                  type="button"
                  disabled={beschaeftigt || props.dateiName === null}
                  onClick={props.onPruefen}
                >
                  {props.vorschau === null ? 'Prüfen' : 'Erneut prüfen'}
                </button>
              </p>
              {props.vorschau !== null && (
                <>
                  <Vorschau vorschau={props.vorschau} />
                  <p>
                    <button
                      type="button"
                      disabled={beschaeftigt || !hatAenderungen(props.vorschau)}
                      onClick={props.onUebernehmen}
                    >
                      Übernehmen
                    </button>{' '}
                    {!hatAenderungen(props.vorschau) && 'Nichts zu übernehmen.'}
                  </p>
                </>
              )}
            </>
          )}

          {props.formular !== null && props.format !== undefined && (
            <FormatFormular {...props.formular} gesperrt={beschaeftigt || props.formular.sendet} />
          )}
        </>
      )}
    </section>
  );
}
